<template>
  <div id="tabs">
    <div class="project-tabs-mobile is-hidden-tablet">
      <label class="label" for="project-section">Project Section</label>
      <div class="select is-fullwidth">
        <select id="project-section" v-model="activeTab">
          <option v-for="tab in visibleTabs" :key="tab.value" :value="tab.value">{{ tab.label }}</option>
        </select>
      </div>
    </div>

    <o-tabs
        :animated="false"
        v-model="activeTab"
    >
      <o-tab-item :label="biddingTabLabel" value="bidding">
        <Bidding :key="`bidding-${bidPackageId}`" :bid-package-id="bidPackageId" @projectBidderUpdated="handleProjectBidderUpdated" :public-project-view="publicProjectView" />
      </o-tab-item>
      <o-tab-item label="Info" value="info">
        <Info :key="`info-${bidPackageId}`" :bid-package-id="bidPackageId" :public-project-view="publicProjectView" />
      </o-tab-item>
      <o-tab-item v-if="isTabVisible('notices')" label="Notices" value="notices">
        <Notices :key="`notices-${bidPackageId}`" :bid-package-id="bidPackageId" :public-project-view="publicProjectView" />
      </o-tab-item>
      <o-tab-item v-if="isTabVisible('contacts')" label="Contacts" value="contacts">
        <Contacts :key="`contacts-${bidPackageId}`" :bid-package-id="bidPackageId" :public-project-view="publicProjectView" />
      </o-tab-item>
      <o-tab-item v-if="isTabVisible('question')" label="Submit Question" value="question">
        <SubmitQuestion :key="`question-${bidPackageId}`" :bid-package-id="bidPackageId" />
      </o-tab-item>
      <o-tab-item v-if="isTabVisible('bid')" label="Submit Bid" value="bid">
        <SubmitBid :key="`bid-${bidPackageId}`" :bid-package-id="bidPackageId" :active="activeTab === 'bid'" @updated="handleBidSubmitted" />
      </o-tab-item>
    </o-tabs>
  </div>
</template>

<script setup lang="ts">

import Bidding from "@/pages/Projects/Bidding.vue";
import Contacts from "@/pages/Projects/Contacts.vue";
import Info from "@/pages/Projects/Info.vue";
import Notices from "@/pages/Projects/Notices.vue";
import SubmitQuestion from "@/pages/Projects/SubmitQuestion.vue";
import SubmitBid from "@/pages/Projects/SubmitBid.vue";

import {useRoute, useRouter} from "vue-router";

import {
  NotificationError,
} from "@/internal";
import {computed, onMounted, ref, toRefs, watch} from "vue";
import {storeToRefs} from "pinia";
import {useInviteStore} from "@/stores/InviteStore";
import {usePublicProjectStore} from "@/stores/PublicProjectStore";
import {useBidPackageStore} from "@/stores/BidPackageStore";
import {useProjectStore} from "@/stores/ProjectStore";
import {useCompanyStore} from "@/stores/CompanyStore";
import {useBidPackageFileStore} from "@/stores/BidPackageFileStore";
import {useBidPackageCompanyFileStore} from "@/stores/BidPackageCompanyFileStore";

const bidPackageStore = useBidPackageStore();
const {bidPackage} = storeToRefs(bidPackageStore);
const projectStore = useProjectStore();
const bidPackageFileStore = useBidPackageFileStore();
const bidPackageCompanyFileStore = useBidPackageCompanyFileStore();
const publicProjectStore = usePublicProjectStore();
const inviteStore = useInviteStore();
const {invites} = storeToRefs(inviteStore);
const route = useRoute();
const router = useRouter();
const activeTab = ref('bidding');
const invitesLoaded = ref(false);
const {company} = storeToRefs(useCompanyStore());

const props = withDefaults(defineProps<{
  bidPackageId: number | null,
  publicProjectView?: boolean,
}>(), {
  bidPackageId: null,
  publicProjectView: false,
});
const {
  bidPackageId,
  publicProjectView,
} = toRefs(props);

const emit = defineEmits<{
  'projectBidderUpdated': [],
}>();

const biddingTabLabel = computed(() => publicProjectView.value ? 'Plans' : 'Bidding');

const project = computed(() => publicProjectView.value ? publicProjectStore.project : projectStore.project);

const projectId = computed(() => parseInt(route.params.projectId as string));

const showSubmitBid = computed(() => Boolean(
  project.value?.show_bid_upload || invites.value?.length,
));

const showSubmitQuestion = computed(() => company.value?.client?.show_rfi);

const activeContent = computed(() => {
  if (Number(bidPackageId.value) > 0) {
    return Number(bidPackage.value?.id) === Number(bidPackageId.value)
      ? bidPackage.value
      : null;
  }

  return project.value;
});

const hasNotices = computed(() => Boolean(activeContent.value?.notices?.length));
const hasContacts = computed(() => Boolean(
  activeContent.value?.estimators?.length || activeContent.value?.project_contacts?.length,
));
const visibilityReady = computed(() => Boolean(
  project.value
  && (publicProjectView.value || company.value)
  && (publicProjectView.value || invitesLoaded.value)
  && (Number(bidPackageId.value) <= 0 || activeContent.value),
));

const visibleTabs = computed(() => [
  {value: 'bidding', label: biddingTabLabel.value},
  {value: 'info', label: 'Info'},
  ...(hasNotices.value ? [{value: 'notices', label: 'Notices'}] : []),
  ...(hasContacts.value ? [{value: 'contacts', label: 'Contacts'}] : []),
  ...(!publicProjectView.value && showSubmitQuestion.value ? [{value: 'question', label: 'Submit Question'}] : []),
  ...(!publicProjectView.value && showSubmitBid.value ? [{value: 'bid', label: 'Submit Bid'}] : []),
]);

const validTabs = computed(() => visibleTabs.value.map(tab => tab.value));

onMounted(loadSelectedContent);

function syncActiveTabFromHash() {
  if (!visibilityReady.value) {
    return;
  }

  const tab = route.hash.replace(/^#/, '');
  activeTab.value = validTabs.value.includes(tab) ? tab : 'bidding';

  if (tab && !validTabs.value.includes(tab)) {
    router.replace({hash: ''});
  }
}

function isTabVisible(tab: string) {
  return validTabs.value.includes(tab);
}

function handleBidSubmitted() {
  handleProjectBidderUpdated();
  loadBidPackageCompanyFiles();
}

function handleProjectBidderUpdated() {
  emit('projectBidderUpdated');
}

async function loadSelectedContent() {
  const selectedBidPackageId = bidPackageId.value;

  if (selectedBidPackageId) {
    bidPackageStore.clear();
    bidPackageFileStore.clear();
    bidPackageCompanyFileStore.clear();
    inviteStore.clearInvites();

    if (!await loadBidPackage(selectedBidPackageId)) {
      return;
    }

    // Ignore a response for a package the user navigated away from while the
    // request was in flight. The newer selection performs its own NDA check.
    if (selectedBidPackageId !== bidPackageId.value) {
      return;
    }
  }

  loadBidPackageFiles();
  loadBidPackageCompanyFiles();

  if (!publicProjectView.value) {
    loadInvites();
  }
}

async function loadBidPackage(selectedBidPackageId: number): Promise<boolean> {
  const payload = {
    params: {
      include: [
        'estimators',
        'notices',
        'projectContacts',
        'trades',
      ],
    }
  };

  try {
    await bidPackageStore.show(selectedBidPackageId, projectId.value, payload);
    return true;
  } catch (error: any) {
    if (selectedBidPackageId !== bidPackageId.value) {
      return false;
    }

    if (error?.code === 'NDA_REQUIRED' && error.nda?.id) {
      window.sessionStorage.setItem('pendingNda', JSON.stringify({
        projectId: projectId.value,
        bidPackageId: selectedBidPackageId,
        nda: error.nda,
        returnPath: route.fullPath,
      }));
      await router.replace({name: 'nda'});
      return false;
    }

    if (error.errors) {
      NotificationError({}, error);
    }
    else {
      NotificationError({
        message: error,
      });
    }

    return false;
  }
}

function loadInvites() {
  invitesLoaded.value = false;

  const payload = {
    params: {
      include: [
      ],
    }
  };

  if (bidPackageId.value) {
    payload.params.bid_package_id = bidPackageId.value;
  }

  inviteStore.index(projectId.value, payload)
      .catch(error => {
        if (error.errors) {
          NotificationError({}, error);
        }
        else {
          NotificationError({
            message: error,
          });
        }
      })
      .finally(() => {
        invitesLoaded.value = true;
      });
}

function loadBidPackageFiles() {
  if (!project.value || (bidPackageId.value === null && !publicProjectView.value)) {
    return;
  }

  const payload = {
    params: {
      include: [
      ],
    }
  };

  bidPackageFileStore.index(projectId.value, bidPackageId.value ?? 0, payload, publicProjectView.value)
      .catch(error => {
        if (error.errors) {
          NotificationError({}, error);
        } else {
          NotificationError({
            message: error,
          });
        }
      });

}

function loadBidPackageCompanyFiles() {
  if (!project.value || bidPackageId.value === null) {
    return;
  }

  const payload = {
    params: {
      include: [
        'trade',
      ],
      file_tree_format: false,
    },
  };

  bidPackageCompanyFileStore.index(projectId.value, bidPackageId.value, payload)
      .catch(error => {
        if (error.errors) {
          NotificationError({}, error);
        } else {
          NotificationError({
            message: error,
          });
        }
      });

}

watch(bidPackageId, loadSelectedContent);

watch([() => route.hash, validTabs, visibilityReady], syncActiveTabFromHash, {immediate: true});

watch(activeTab, (tab) => {
  const hash = tab === 'bidding' ? '' : `#${tab}`;
  if (route.hash !== hash) {
    router.replace({ hash });
  }
});

</script>

<style lang="scss" scoped>

// Oruga applies these classes when a tab pane is activated.  Keep project
// content changes instantaneous, even if the component's animation setting is
// overridden by a global theme configuration.
#tabs :deep(.slide-next-enter-active),
#tabs :deep(.slide-next-leave-active),
#tabs :deep(.slide-prev-enter-active),
#tabs :deep(.slide-prev-leave-active) {
  transition: none !important;
}
</style>

<style lang="scss">

#tabs {
  width: 100%;

  .project-tabs-mobile {
    margin-bottom: 1em;
  }

  .b-tabs {
    .tab-content {
      padding-top: 1em;
      padding-left: 1em;
    }

    .tabs {
      border-bottom: 1px solid $grey-lighter;
      white-space: nowrap !important;

      div a {
        border: 0;
        border-bottom: 2px solid transparent;
        border-radius: 0;
        margin-bottom: -1px;

        &.is-active {
          border-bottom-color: $primary;
          color: $link !important;
        }
      }
    }
  }

  @media screen and (max-width: ($tablet - 1px)) {
    .b-tabs {
      > .tabs {
        display: none;
      }

      .tab-content {
        padding-left: 0;
        padding-top: 0;
      }
    }
  }

}

</style>
